import { useEffect, useState } from "react";
import { Modal, Box } from "@mui/material";

import { Navigations, NavItem } from "../models/userData.model";
import Button, { ButtonType } from "./Assets/Button";
import { RootDispatch, RootState } from "../store";
import { useDispatch, useSelector } from "react-redux";
import { getAllNavigation } from "../store/slices/userSlice";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  navigations: Navigations | undefined;
  setNavigations: (nav: Navigations) => void;
  onSave?: (nav: Navigations) => void;
}

const NavigationAccessModal: React.FC<Props> = ({
  isOpen,
  onClose,
  navigations,
  setNavigations,
  onSave,
}) => {
  const dispatch: RootDispatch = useDispatch();
  const allNavigation = useSelector(
    (state: RootState) => state.users.allNavigation,
  );

  useEffect(() => {
    dispatch(getAllNavigation());
  }, [dispatch]);

  const [selected, setSelected] = useState<Record<string, NavItem[]>>({
    main: [],
    extras: [],
    frelit: [],
  });

  const toggleNav = (nav: NavItem, section: string) => {
    setSelected((prev) => {
      const updated = { ...prev };

      Object.keys(updated).forEach((key) => {
        updated[key] = updated[key]?.filter((n) => n._id !== nav._id);
      });

      const exists = prev[section]?.some((n) => n._id === nav._id);

      if (!exists) {
        updated[section] = [...(updated[section] || []), nav];
      }

      return updated;
    });
  };
  useEffect(() => {
    if (navigations && allNavigation.length) {
      const mapIdsToNav = (ids: string[] = []) =>
        ids
          .map((id) => allNavigation.find((nav) => nav._id === id))
          .filter((nav): nav is NavItem => !!nav);

      setSelected({
        main: mapIdsToNav(navigations.main),
        extras: mapIdsToNav(navigations.extras),
        frelit: mapIdsToNav(navigations.frelit),
      });
    }
  }, [navigations, allNavigation]);

  const saveAccess = () => {
    const convertToIds = (arr: NavItem[]) =>
      arr.map((item) => item._id).filter((id): id is string => !!id);

    const allSelected = [
      ...(selected.main || []),
      ...(selected.extras || []),
      ...(selected.frelit || []),
    ];

    const leadsSelected = allSelected.some((nav) => nav.component === "CRM");
    const finalNav = {
      main: convertToIds(selected.main || []),
      extras: convertToIds(selected.extras || []),
      frelit: convertToIds(selected.frelit || []),
      notifications: leadsSelected
        ? allNavigation.find((n) => n.component === "NotificationPage")?._id ||
          null
        : null,
      customerNotifications: leadsSelected
        ? allNavigation.find((n) => n.component === "CustomerNotificationsPage")
            ?._id || null
        : null,
    };

    if (onSave) {
      onSave(finalNav);
    } else {
      setNavigations(finalNav);
    }

    onClose();
  };

  const isCRMSelected = [
    ...(selected.main || []),
    ...(selected.extras || []),
    ...(selected.frelit || []),
  ].some((nav) => nav.component === "CRM");

  const notificationNav = allNavigation.find(
    (n) => n.component === "NotificationPage",
  );

  const customerNotificationNav = allNavigation.find(
    (n) => n.component === "CustomerNotificationsPage",
  );

  return (
    <Modal open={isOpen} onClose={onClose}>
      <Box
        sx={{
          width: 750,
          margin: "4% auto",
          maxHeight: "85vh",
        }}
        className="bg-white rounded-xl shadow-2xl flex flex-col text-xs"
      >
        <div className="border-b px-6 py-4">
          <h2 className="text-xl font-semibold text-gray-800">
            Navigation Access
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Select which modules this user can access
          </p>
        </div>

        <div className="p-6 overflow-y-auto space-y-6">
          {["main", "extras", "frelit"].map((section) => (
            <div key={section} className="border rounded-lg bg-gray-50 p-4">
              <h3 className="font-semibold text-gray-700 capitalize mb-3">
                {section} Navigation
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {allNavigation.map((nav) => {
                  const isChecked = selected?.[section]?.some(
                    (n) => n._id === nav._id,
                  );

                  return (
                    <label
                      key={nav.component}
                      className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer transition 
                      ${
                        isChecked
                          ? "bg-blue-50 border-blue-400"
                          : "bg-white hover:border-blue-300"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked || false}
                        onChange={() => toggleNav(nav, section)}
                        className="accent-blue-600 cursor-pointer"
                      />

                      {nav.label}
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        <div className="border rounded-lg bg-gray-50 p-4">
          <h3 className="font-semibold text-gray-700 mb-3">
            Notifications (Auto Assigned)
          </h3>

          <div className="grid grid-cols-2 gap-3">
            {[notificationNav, customerNotificationNav].map((nav) => {
              if (!nav) return null;

              return (
                <div
                  key={nav._id}
                  className={`flex items-center gap-2 px-3 py-2 rounded-md border 
          ${
            isCRMSelected
              ? "bg-green-50 border-green-400"
              : "bg-gray-100 border-gray-300 opacity-60"
          }`}
                >
                  <input
                    type="checkbox"
                    checked={isCRMSelected}
                    readOnly
                    className="accent-green-600 cursor-not-allowed"
                  />
                  <span>{nav.label}</span>
                </div>
              );
            })}
          </div>

          <p className="text-xs text-gray-500 mt-2">
            Automatically enabled when Leads access is selected.
          </p>
        </div>

        <div className="border-t px-6 py-4 flex justify-end gap-3 bg-gray-50 rounded-b-xl">
          <Button
            type={ButtonType.Cancel}
            onClick={onClose}
            className="text-xs"
          >
            Cancel
          </Button>
          <Button
            type={ButtonType.Submit}
            onClick={saveAccess}
            className="text-xs"
          >
            Save
          </Button>
        </div>
      </Box>
    </Modal>
  );
};

export default NavigationAccessModal;
